import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import {
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  Building2,
  ShieldCheck,
  RotateCcw,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { hospitalService } from '@/services/hospitalService';
import { clinicService } from '@/services/clinicService';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const { hospitalId: routeHospitalId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const {
    verifiedHospital,
    setVerifiedHospital,
    clearVerifiedHospital,
    setActiveClinicId,
  } = useClinic();

  const queryHospitalId = searchParams.get('hospitalId') || routeHospitalId;
  const queryClinicId = searchParams.get('clinicId');

  const effectiveHospitalId = queryHospitalId || verifiedHospital?.id || '';

  const [step, setStep] = useState(effectiveHospitalId ? 'LOGIN' : 'HOSPITAL');
  const [hospitalIdInput, setHospitalIdInput] = useState(effectiveHospitalId);
  const [isVerifyingHospital, setIsVerifyingHospital] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Auto-verify queryHospitalId only if it differs from current verifiedHospital
  useEffect(() => {
    let isMounted = true;

    if (queryHospitalId) {
      if (!verifiedHospital || verifiedHospital.id !== queryHospitalId) {
        setIsVerifyingHospital(true);
        hospitalService
          .verifyHospital(queryHospitalId)
          .then((verified) => {
            if (!isMounted) return;
            setVerifiedHospital(verified);
            setHospitalIdInput(verified.id);
            setStep('LOGIN');
          })
          .catch((err) => {
            if (!isMounted) return;
            setErrorMessage(err.message || `Hospital ID "${queryHospitalId}" not found.`);
            setStep('HOSPITAL');
          })
          .finally(() => {
            if (isMounted) setIsVerifyingHospital(false);
          });
      } else {
        setHospitalIdInput(verifiedHospital.id);
        setStep('LOGIN');
      }
    } else if (verifiedHospital) {
      setHospitalIdInput(verifiedHospital.id);
      setStep('LOGIN');
    }

    return () => {
      isMounted = false;
    };
  }, [queryHospitalId, verifiedHospital?.id, setVerifiedHospital]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const targetHospId = effectiveHospitalId || '1001';
    if (queryClinicId) {
      return <Navigate to={`/hospital/${targetHospId}/clinic/${queryClinicId}/dashboard`} replace />;
    }
    return <Navigate to={`/hospital/${targetHospId}/select-clinic`} replace />;
  }

  // Handle Hospital Verification Step
  const handleHospitalSubmit = async (e) => {
    e.preventDefault();
    const cleanId = hospitalIdInput.trim();
    if (!cleanId || cleanId.length < 4) return;

    setIsVerifyingHospital(true);
    setErrorMessage('');

    try {
      const verified = await hospitalService.verifyHospital(cleanId);
      setVerifiedHospital(verified);
      setStep('LOGIN');
      
      const newParams = new URLSearchParams(searchParams);
      newParams.set('hospitalId', verified.id);
      setSearchParams(newParams, { replace: true });
    } catch (err) {
      setErrorMessage(err.message || `Hospital ID "${cleanId}" not found.`);
    } finally {
      setIsVerifyingHospital(false);
    }
  };

  // Handle Sign In Step
  const onLoginSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    const targetHosp = verifiedHospital?.id || queryHospitalId || hospitalIdInput.trim();
    if (!targetHosp) {
      setErrorMessage('Hospital ID is mandatory. Please specify your Hospital ID.');
      setIsLoading(false);
      return;
    }

    try {
      const authUser = await login(data.username, data.password, targetHosp);
      const targetHospitalId = authUser?.hospitalId || targetHosp;
      toast.success(`Welcome back, ${authUser?.firstName || authUser?.username || 'Staff Member'}!`);

      // If clinicId was provided in URL query param, skip clinic selection and go to dashboard
      if (queryClinicId) {
        setActiveClinicId(queryClinicId);
        clinicService.syncClinic(targetHospitalId, { id: queryClinicId }).catch(() => {});
        navigate(`/hospital/${targetHospitalId}/clinic/${queryClinicId}/dashboard`, { replace: true });
        return;
      }

      // If user only has 1 assigned clinic, auto-redirect to dashboard
      const assigned = authUser?.assignedClinics;
      if (Array.isArray(assigned) && assigned.length === 1) {
        const singleClinic = assigned[0];
        const singleClinicId = typeof singleClinic === 'string' ? singleClinic : (singleClinic.id || singleClinic.clinicId);
        setActiveClinicId(singleClinicId);
        clinicService.syncClinic(targetHospitalId, typeof singleClinic === 'object' ? singleClinic : { id: singleClinicId }).catch(() => {});
        navigate(`/hospital/${targetHospitalId}/clinic/${singleClinicId}/dashboard`, { replace: true });
        return;
      }

      // Otherwise navigate to clinic selection
      navigate(`/hospital/${targetHospitalId}/select-clinic`);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (error.response?.status === 401 ? 'Invalid username or password.' : null) ||
        error.message ||
        'Authentication failed. Please check your credentials.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeHospital = () => {
    clearVerifiedHospital();
    setErrorMessage('');
    setHospitalIdInput('');
    setStep('HOSPITAL');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('hospitalId');
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-950" data-testid="login-page">
      {/* Left Column - Visual Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105 opacity-35"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/85 to-teal-950/70" />

        <div className="relative z-10 p-12 flex flex-col justify-between h-full text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/30 text-slate-950 font-bold">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <span className="font-heading font-bold text-2xl text-white tracking-tight">PulseFlow</span>
              <p className="text-xs text-teal-400 font-semibold uppercase tracking-wider">
                Outpatient Clinical Network
              </p>
            </div>
          </div>

          <div className="max-w-md space-y-4">
            {step === 'LOGIN' && (verifiedHospital?.id || effectiveHospitalId) ? (
              <>
                <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 text-xs px-3 py-1">
                  Hospital Entity #{verifiedHospital?.id || effectiveHospitalId}
                </Badge>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white leading-snug">
                  {verifiedHospital?.name || 'Hospital Clinical Portal'}
                </h1>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Unified hospital portal for physicians, clinical triage, outpatient appointment scheduling, and electronic medical records.
                </p>
              </>
            ) : (
              <>
                <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 text-xs px-3 py-1">
                  Tenant Gateway & Verification
                </Badge>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white leading-snug">
                  Access Your Hospital & Outpatient Center
                </h1>
                <p className="text-sm text-slate-300 leading-relaxed">
                  PulseFlow securely verifies your hospital entity before routing clinical staff, physicians, and triage officers to your designated outpatient portal.
                </p>
              </>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-800/80 pt-4">
            <span>© 2026 PulseFlow Healthcare Solutions</span>
            <span className="flex items-center gap-1.5 text-teal-400">
              <ShieldCheck className="h-4 w-4" /> ABDM & HIPAA Aligned
            </span>
          </div>
        </div>
      </div>

      {/* Right Column - Form Container */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md space-y-6">
          {/* Hospital Header Pill when in LOGIN step */}
          {step === 'LOGIN' && (verifiedHospital?.name || effectiveHospitalId) && (
            <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-200 shadow-sm text-xs">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center font-bold">
                  <Building2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 truncate max-w-[200px]">
                    {verifiedHospital?.name || 'Connected Hospital'}
                  </p>
                  <p className="text-[11px] text-slate-500 font-mono">Hospital #{verifiedHospital?.id || effectiveHospitalId}</p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleChangeHospital}
                className="text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 h-7"
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Change
              </Button>
            </div>
          )}

          {/* Light Card */}
          <Card className="border border-slate-200/80 bg-white text-slate-900 rounded-2xl shadow-xl">
            <CardHeader className="space-y-1 pb-4 text-center">
              <CardTitle className="text-2xl font-heading font-bold text-slate-900">
                {step === 'HOSPITAL' ? 'Enter Hospital ID' : 'Sign In'}
              </CardTitle>
              {step === 'LOGIN' && verifiedHospital?.name && (
                <p className="text-xs text-teal-600 font-semibold">{verifiedHospital.name}</p>
              )}
            </CardHeader>

            <CardContent className="space-y-4">
              {errorMessage && (
                <div className="p-3.5 rounded-xl bg-red-50 border border-red-200/80 text-red-700 text-xs flex items-start gap-2.5 shadow-sm animate-in fade-in duration-200">
                  <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                  <div className="flex-1 leading-relaxed">{errorMessage}</div>
                </div>
              )}

              {step === 'HOSPITAL' ? (
                /* Step 1: Minimalist Hospital ID Form */
                <form onSubmit={handleHospitalSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Input
                      id="hospitalId"
                      type="text"
                      placeholder="Enter Hospital ID"
                      value={hospitalIdInput}
                      onChange={(e) => {
                        setHospitalIdInput(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      className="h-11 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500 text-sm font-mono text-center tracking-wider"
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={hospitalIdInput.trim().length < 4 || isVerifyingHospital}
                    className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold h-10 text-xs shadow-md transition-all"
                  >
                    {isVerifyingHospital ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </form>
              ) : (
                /* Step 2: Minimalist Sign In Form */
                <form onSubmit={form.handleSubmit(onLoginSubmit)} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="username" className="text-xs font-semibold text-slate-700">
                      Username
                    </Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="Enter username"
                      className="h-10 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500 text-xs font-mono"
                      {...form.register('username')}
                      onChange={(e) => {
                        form.register('username').onChange(e);
                        if (errorMessage) setErrorMessage('');
                      }}
                      data-testid="input-username"
                      autoFocus
                    />
                    {form.formState.errors.username && (
                      <p className="text-xs text-red-500">{form.formState.errors.username.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-xs font-semibold text-slate-700">
                        Password
                      </Label>
                      <Link to="/forgot-password" className="text-[11px] text-teal-600 hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter password"
                        className="pr-10 h-10 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-teal-500 text-xs"
                        {...form.register('password')}
                        onChange={(e) => {
                          form.register('password').onChange(e);
                          if (errorMessage) setErrorMessage('');
                        }}
                        data-testid="input-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {form.formState.errors.password && (
                      <p className="text-xs text-red-500">{form.formState.errors.password.message}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-10 text-xs shadow-md transition-all"
                    data-testid="button-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <div className="pt-2 text-center">
                    <button
                      type="button"
                      onClick={handleChangeHospital}
                      className="text-xs text-slate-500 hover:text-teal-600 transition-colors"
                    >
                      Change Hospital
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
